import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  LayoutDashboard, Inbox as InboxIcon, Users2, Building2, GitBranch, Mail,
  Ticket, UserCheck, FileText, Book, History, Settings as SettingsIcon,
  Bot, LogOut, ChevronDown, Send, GitPullRequestArrow, CreditCard, TrendingUp,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/hooks/useTheme";
import NotificationsBell from "@/components/NotificationsBell";
import TopBar from "@/components/TopBar";

const NAV = [
  { section: "Workspace" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/inbox", label: "Inbox", icon: InboxIcon },
  { to: "/approvals", label: "Approvals", icon: UserCheck, badgeKey: "pending_approvals" },
  { to: "/tickets", label: "Tickets", icon: Ticket },
  { to: "/contacts", label: "Contacts", icon: Users2 },

  { section: "Bulk email" },
  { to: "/bulk", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/bulk/analytics", label: "Analytics", icon: TrendingUp },
  { to: "/bulk/campaigns", label: "Campaigns", icon: Send },
  { to: "/bulk/templates", label: "Templates", icon: FileText },
  { to: "/bulk/smtp-profiles", label: "SMTP Profiles", icon: Mail },
  { to: "/bulk/suppressions", label: "Suppressions", icon: History },
  { to: "/bulk/logs", label: "Delivery Log", icon: History },

  { section: "AI & Content" },
  { to: "/agents", label: "Department Agents", icon: Bot },
  { to: "/rules", label: "Routing rules", icon: GitPullRequestArrow },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/signatures", label: "Signatures", icon: FileText },
  { to: "/knowledge", label: "Knowledge Base", icon: Book },

  { section: "Administration" },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/departments", label: "Departments", icon: GitBranch },
  { to: "/email-accounts", label: "Email Accounts", icon: Mail },
  { to: "/users", label: "Users & Roles", icon: Users2 },
  { to: "/billing", label: "Plans & Usage", icon: CreditCard },
  { to: "/audit-logs", label: "Audit Logs", icon: History },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function AppShell() {
  const { user, logout, activeCompanyId, setActiveCompanyId } = useAuth();
  useTheme();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [kpis, setKpis] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data);
        if (!activeCompanyId && data.length && user?.role !== "super_admin") {
          setActiveCompanyId(data[0].id);
        }
      } catch { /* ignore */ }
    })();
  }, [user, activeCompanyId, setActiveCompanyId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/kpis", {
          params: activeCompanyId ? { company_id: activeCompanyId } : {},
        });
        setKpis(data);
      } catch { /* ignore */ }
    })();
  }, [activeCompanyId]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);

  const onLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[248px_1fr] bg-background text-foreground" data-testid="app-shell">
      <aside className="border-r border-border bg-card flex flex-col relative" data-testid="app-sidebar">
        <div className="px-4 py-5 border-b border-border">
          <motion.img
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            src="/brand/relay-wordmark-640.png"
            alt="Relay CRM"
            className="h-14 w-auto object-contain select-none"
            data-testid="app-logo"
            draggable={false}
          />
        </div>

        <div className="p-3 border-b border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center justify-between px-3 py-2.5 border border-border rounded-xl bg-muted/40 hover:bg-muted transition-colors"
                data-testid="company-switcher"
              >
                <div className="text-left">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Workspace</div>
                  <div className="text-sm font-medium truncate max-w-[160px]">
                    {activeCompany ? (activeCompany.trading_name || activeCompany.legal_name) : (user?.role === "super_admin" ? "All companies" : "Select company")}
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 rounded-xl">
              <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest">Workspaces</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {user?.role === "super_admin" && (
                <DropdownMenuItem onClick={() => setActiveCompanyId("")} data-testid="company-switch-all">All companies</DropdownMenuItem>
              )}
              {companies.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => setActiveCompanyId(c.id)} data-testid={`company-switch-${c.id}`}>
                  {c.trading_name || c.legal_name}
                </DropdownMenuItem>
              ))}
              {companies.length === 0 && <DropdownMenuItem disabled>No companies yet</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/companies")}>Manage companies</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 no-scrollbar">
          {NAV.map((item, idx) =>
            item.section ? (
              <div key={idx} className="px-3 pt-4 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {item.section}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
              >
                <item.icon className="h-4 w-4" strokeWidth={1.75} />
                <span className="flex-1">{item.label}</span>
                {item.badgeKey && kpis[item.badgeKey] > 0 && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-destructive text-white">
                    {kpis[item.badgeKey]}
                  </span>
                )}
              </NavLink>
            ),
          )}
          {activeCompany && (
            <NavLink
              to={`/companies/${activeCompany.id}`}
              className={({ isActive }) => `side-link ${isActive ? "active" : ""} mt-1`}
              data-testid="nav-company-detail"
              title={`View ${activeCompany.trading_name || activeCompany.legal_name} details`}
            >
              <Building2 className="h-4 w-4 text-emerald-brand" strokeWidth={1.75} />
              <span className="flex-1 truncate">{activeCompany.trading_name || activeCompany.legal_name}</span>
              <span className="text-[9px] font-mono text-muted-foreground uppercase">info</span>
            </NavLink>
          )}
        </nav>

        <div className="border-t border-border p-3 flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-muted rounded-lg p-1.5 transition-colors" data-testid="user-menu-trigger">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-brand to-sun grid place-items-center text-white text-xs font-semibold">
                  {user?.username?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{user?.full_name || user?.username}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate">{user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl">
              <DropdownMenuLabel>Signed in as {user?.username}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NotificationsBell />
        </div>
      </aside>

      <main className="min-h-screen bg-background flex flex-col overflow-hidden" data-testid="app-main">
        <TopBar />
        <Outlet />
      </main>
    </div>
  );
}
